'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface HeaderProps {
  cartCount?: number;
}

const Header = ({ cartCount = 0 }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navigationItems = [
    { label: 'Главная', path: '/homepage', icon: 'HomeIcon' },
    { label: 'Каталог', path: '/product-catalog', icon: 'Squares2X2Icon' },
    { label: 'Корзина', path: '/shopping-cart', icon: 'ShoppingCartIcon', badge: cartCount },
  ];

  const isActivePath = (path: string) => {
    return pathname === path;
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[100] bg-card transition-all duration-250 ${
          isScrolled ? 'shadow-md' : 'shadow-sm'
        }`}
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex h-[60px] items-center justify-between px-4 lg:h-[60px] lg:px-6">
            {/* Logo */}
            <Link
              href="/homepage"
              className="flex items-center gap-2 transition-transform duration-250 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
              onClick={handleNavClick}
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="text-primary-foreground"
                >
                  <path
                    d="M3 9L12 2L21 9V20C21 20.5304 20.7893 21.0391 20.4142 21.4142C20.0391 21.7893 19.5304 22 19 22H5C4.46957 22 3.96086 21.7893 3.58579 21.4142C3.21071 21.0391 3 20.5304 3 20V9Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 22V12H15V22"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span className="font-heading text-xl font-bold text-primary lg:text-2xl">
                Optovichok.kz
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-8 lg:flex">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={`relative flex items-center gap-2 font-heading text-base font-medium transition-all duration-250 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 ${
                    isActivePath(item.path)
                      ? 'text-primary' :'text-text-secondary hover:text-primary'
                  }`}
                >
                  <Icon name={item.icon as any} size={20} />
                  <span>{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="absolute -right-4 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-accent px-1.5 text-xs font-medium text-accent-foreground">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                  {isActivePath(item.path) && (
                    <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent" />
                  )}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={handleMobileMenuToggle}
              className="flex h-10 w-10 items-center justify-center rounded-md text-text-primary transition-colors duration-250 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 lg:hidden"
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-[200] bg-background lg:hidden"
          onClick={handleMobileMenuToggle}
        >
          <div
            className="fixed right-0 top-0 h-full w-full max-w-sm bg-card shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-[60px] items-center justify-between border-b border-border px-4">
              <span className="font-heading text-lg font-semibold text-primary">
                Меню
              </span>
              <button
                onClick={handleMobileMenuToggle}
                className="flex h-10 w-10 items-center justify-center rounded-md text-text-primary transition-colors duration-250 hover:bg-muted focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2"
                aria-label="Close mobile menu"
              >
                <Icon name="XMarkIcon" size={24} />
              </button>
            </div>

            <nav className="flex flex-col gap-2 p-4">
              {navigationItems.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  onClick={handleNavClick}
                  className={`flex items-center gap-3 rounded-md px-4 py-3 font-heading text-base font-medium transition-all duration-250 active:scale-[0.97] ${
                    isActivePath(item.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-text-primary hover:bg-muted'
                  }`}
                >
                  <Icon name={item.icon as any} size={24} />
                  <span className="flex-1">{item.label}</span>
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-accent px-2 text-xs font-medium text-accent-foreground">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Spacer to prevent content from going under fixed header */}
      <div className="h-[60px]" />
    </>
  );
};

export default Header;